import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource } from '@gamelearn/skeletor/models';
import { DropdownQuestion, QuestionBase, QuestionGroup } from 'core/components/forms';
import { ListHelperService } from 'core/services/list-helper.service';
import { LocalesService } from 'core/services/locales.service';

const durationOptions = [
  { key: '0-59', value: '< 1h' },
  { key: '60-119', value: '1h - 2h' },
  { key: '120-239', value: '2h - 4h' },
  { key: '240-100000', value: '> 4h' }
];
const itineraryDurationOptions = [
  { key: '0-179', value: '< 3h' },
  { key: '180-299', value: '3h - 5h' },
  { key: '300-599', value: '5h - 10h' },
  { key: '600-1000000', value: '> 10h' }
];

@Injectable()
export class CoursesFiltersQuestionsService {
  public filterNamesMap = {
    accessMode: (params) => this.getAccessModeFilter(params),
    languages: (params) => this.getLanguageFilter(params),
    duration: (params) => this.getDurationFilter(params, durationOptions),
    itineraryDuration: (params) => this.getDurationFilter(params, itineraryDurationOptions),
    status: (params) => this.getStatusFilter(params),
    shouldSendDiploma: (params) => this.getShouldSendDiplomaFilter(params),
    default: (filterName, dataSource, params) => this.getDefaultFilter(filterName, dataSource, params)
  };
  constructor(
    public translate: TranslateService,
    public locales: LocalesService,
    private listHelper: ListHelperService
  ) {}

  getFilterQuestions(filterList: string[], dataSource: TableDatasource): QuestionBase<any>[] {
    const params = {
      class: 'form-custom-grid--item col-3 gap-left-right margin-bottom'
    };
    const filtersQuestionGroup = new QuestionGroup({
      disableGrid: true,
      gridClass: 'form-custom-grid',
      childQuestions: filterList
        .map((filterName) => {
          return this.filterNamesMap[filterName]
            ? this.filterNamesMap[filterName](params)
            : this.filterNamesMap.default(filterName, dataSource, params);
        })
        .filter((question) => !!question)
    });
    return [filtersQuestionGroup];
  }

  getDefaultFilter(filterName, dataSource, params) {
    if (filterName) {
      let column = dataSource.columns.find((col) => col.source === filterName);
      column = column ? column : { source: filterName, allowFiltering: true };
      return this.listHelper.createQuestionFromColumn(column, params);
    }
    return null;
  }

  getAccessModeFilter(params) {
    return new DropdownQuestion({
      key: 'accessMode',
      label: 'courses.headerTable.access',
      value: 'any',
      options: [
        { key: 'any', value: this.translate.instant('properties.all') },
        { key: 'open', value: this.translate.instant('courses.config.accessModes.open') },
        { key: 'limited', value: this.translate.instant('courses.config.accessModes.limited') },
        { key: 'unavailable', value: this.translate.instant('courses.config.accessModes.unavailable') },
        { key: 'default', value: this.translate.instant('courses.config.accessModes.defaultCatalogue') }
      ],
      ...params
    });
  }

  getDurationFilter(params, options) {
    return new DropdownQuestion({
      key: 'duration',
      label: 'courses.headerTable.duration',
      value: 'any',
      options: [{ key: 'any', value: this.translate.instant('properties.all') }, ...options],
      ...params
    });
  }

  getStatusFilter(params) {
    return new DropdownQuestion({
      key: 'status',
      label: 'itineraries.headerTable.status',
      value: 'any',
      options: [
        { key: 'any', value: this.translate.instant('properties.all') },
        { key: 'publish', value: this.translate.instant('itineraries.publishLabel') },
        { key: 'draft', value: this.translate.instant('itineraries.draftLabel') },
        { key: 'withdrawn', value: this.translate.instant('itineraries.withdrawnItinerary') }
      ],
      ...params
    });
  }

  getShouldSendDiplomaFilter(params) {
    return new DropdownQuestion({
      key: 'shouldSendDiploma',
      label: 'games.properties.shouldSendDiploma',
      value: 'any',
      options: [
        { key: 'any', value: this.translate.instant('properties.all') },
        { key: 'true', value: this.translate.instant('nouns.yes') },
        { key: 'false', value: this.translate.instant('nouns.no') }
      ],
      ...params
    });
  }

  getLanguageFilter(params?: any) {
    return new DropdownQuestion({
      key: 'languages',
      label: 'nouns.languages',
      placeholder: 'actions.selectLanguages',
      optionsObservable: this.locales.getOptionsObservable(),
      multiple: true,
      ...params
    });
  }
}
